import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { cx } from "utils/join-classnames";
import { Button, PasswordField } from "ui/components";
import {
    logoutUser,
    UpdateUserFields,
    updateLoggedIn,
} from "core/services/authserver/user";
import { useSessionContext } from "ui/context/SessionContext";
import { useLivestreamContext } from "ui/context/LivestreamContext";
import { listProjects } from "core/services/site/project";
import { Project } from "core/models";
import { useNotifications } from "ui/context/NotificationsContext";
import styles from "./Profile.module.scss";

const Profile: React.FC = () => {
    const navigate = useNavigate();
    const sessionContext = useSessionContext();
    const livestreamContext = useLivestreamContext();
    const [currentPassword, setCurrentPassword] = useState<string>();
    const [newPassword, setNewPassword] = useState<string>();
    const [newPasswordConfirm, setNewPasswordConfirm] = useState<string>();
    const [projects, setProjects] = useState<Project[]>([]);

    const { addNotification } = useNotifications();

    const logout = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            await logoutUser(sessionContext.session.token);
            livestreamContext.removeAll();
            sessionContext.setSession(null);
            navigate("/login");
        } catch (err) {
            addNotification(
                "error",
                "Failed to log out",
                "please try again",
                5000,
            );
        }
    };

    const save = async () => {
        if (newPassword !== newPasswordConfirm) {
            addNotification(
                "error",
                "Passwords didn't match",
                "Please try again",
                5000,
            );
            return;
        }
        if (!currentPassword || currentPassword === "") {
            addNotification(
                "error",
                "Fill in your current password",
                "We really do need it",
                5000,
            );
            return;
        }
        if (
            !newPassword ||
            newPassword === "" ||
            !newPasswordConfirm ||
            newPasswordConfirm === ""
        ) {
            addNotification(
                "error",
                "Please fill in all fields",
                "We need all the info we can get",
                5000,
            );
            return;
        }
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            const updateObject: UpdateUserFields = {
                currentPassword,
                password: newPassword,
            };
            const updateUser = await updateLoggedIn(
                sessionContext.session.token,
                updateObject,
            );
            if (updateUser.error) {
                throw Error(updateUser.error);
            }
            addNotification(
                "success",
                `password successfully changed`,
                "Please make sure you remember it",
                0,
            );
        } catch (error) {
            addNotification("error", `[ERROR] ${error}`, "error", 0);
        }
    };

    const init = async () => {
        try {
            if (!sessionContext.session) {
                throw Error("missing session");
            }
            const listProjectsResult = await listProjects(
                sessionContext.session.token,
                100,
            );

            if (listProjectsResult.error || !listProjectsResult.value) {
                throw new Error("error");
            }
            setProjects(listProjectsResult.value);
        } catch (err) {
            addNotification(
                "error",
                "Oops",
                "Something went wrong getting yout projects and info",
                10000,
            );
        }
    };

    useEffect(() => {
        init();
    }, []);
    return (
        <div className={styles.section}>
            <div className={styles.heading}>
                <div className={styles.heading__left}>
                    <h1 className={cx(styles.heading, styles.heading__primary)}>
                        Account settings
                    </h1>
                    <span className={styles.heading__desc}>
                        General account settings.
                    </span>
                </div>
                <div className={styles.heading__right}>
                    <Button type="primary" onClick={logout}>
                        Log out
                    </Button>
                </div>
            </div>

            <div className={styles.barheading}>Profile</div>
            <div className={styles.tabledata__row}>
                <div>Logged in as</div>
                <div>
                    {sessionContext.session?.user.username}{" "}
                    {!sessionContext.verified && "(not verified)"}
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div>Project(s)</div>
                <div>
                    <ul className={styles.projectslist}>
                        {projects.map((project) => (
                            <li key={project.id}>{project.name}</li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div>Registered</div>
                <div>
                    {sessionContext.session &&
                        format(
                            new Date(sessionContext.session.user.creationDate),
                            "dd MMMM yyyy",
                        )}
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div>SpotOn Version</div>
                <div>
                    {
                        SPOTON_APPVERSION
                        // Global const defined in vite.config.ts, source: package.json
                    }
                </div>
            </div>
            <div className={styles.barheading}>Change your password</div>
            <div className={styles.tabledata__row}>
                <div>Current password</div>
                <div>
                    <PasswordField
                        theme="light"
                        defaultText="Current password"
                        onValueChanged={(val) => setCurrentPassword(val)}
                    />
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div>New password</div>
                <div>
                    <PasswordField
                        theme="light"
                        defaultText="New password"
                        onValueChanged={(val) => setNewPassword(val)}
                    />
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div>Confirm new password</div>
                <div>
                    <PasswordField
                        theme="light"
                        defaultText="Confirm new password"
                        onValueChanged={(val) => setNewPasswordConfirm(val)}
                    />
                </div>
            </div>
            <div className={styles.tabledata__row}>
                <div />
                <div>
                    <Button type="secondary" theme="dark" onClick={save}>
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default Profile;
